import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiEdit2, FiPackage, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';
import {
  addCatalogItem,
  deleteCatalogItem,
  getCatalogItems,
  getSchemeOptions,
  uploadCatalogImage,
  updateCatalogItem,
} from '../services/api';

const emptyForm = {
  schemeId: 0,
  itemName: '',
  description: '',
  imageUrl: '',
  mrp: 0,
  pointsRequired: 0,
  stockQty: 0,
  userType: 'both',
};

const RedeemableProducts: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [schemes, setSchemes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [schemeId, setSchemeId] = useState('');
  const [userType, setUserType] = useState('');
  const [status, setStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<any>(emptyForm);

  const loadSchemes = useCallback(async () => {
    try {
      const res = await getSchemeOptions();
      setSchemes(res.data.data ?? []);
    } catch (error) {
      console.error(error);
      toast.error('Unable to load scheme options');
    }
  }, []);

  const loadItems = useCallback(async (searchValue = search) => {
    try {
      setLoading(true);
      const res = await getCatalogItems({
        page,
        pageSize: 20,
        search: searchValue,
        schemeId,
        userType,
        status,
      });
      setItems(res.data.data ?? []);
      setTotalCount(res.data.totalCount ?? 0);
    } catch (error) {
      console.error(error);
      toast.error('Unable to load redeemable products');
    } finally {
      setLoading(false);
    }
  }, [page, schemeId, search, status, userType]);

  useEffect(() => {
    loadSchemes();
  }, [loadSchemes]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleSearch = () => {
    setPage(1);
    loadItems(search);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (item: any) => {
    setEditing(item);
    setForm({
      schemeId: item.schemeId,
      itemName: item.itemName,
      description: item.description || '',
      imageUrl: item.imageUrl || '',
      mrp: item.mrp || 0,
      pointsRequired: item.pointsRequired,
      stockQty: item.stockQty,
      userType: item.userType,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.schemeId || !form.itemName.trim()) {
      toast.error('Scheme and product name are required');
      return;
    }

    try {
      setSaving(true);
      if (editing) {
        await updateCatalogItem(editing.catalogId, form);
        toast.success('Redeemable product updated');
      } else {
        await addCatalogItem(form);
        toast.success('Redeemable product added');
      }
      setShowModal(false);
      setForm(emptyForm);
      loadItems();
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Unable to save redeemable product');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (file?: File | null) => {
    if (!file) {
      return;
    }

    try {
      setSaving(true);
      const res = await uploadCatalogImage(file);
      setForm((current: any) => ({
        ...current,
        imageUrl: res.data.data.imageUrl,
      }));
      toast.success('Image uploaded');
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Unable to upload image');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id: number) => {
    if (!window.confirm('Deactivate this redeemable product?')) {
      return;
    }

    try {
      await deleteCatalogItem(id);
      toast.success('Redeemable product deactivated');
      loadItems();
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Unable to deactivate product');
    }
  };

  const totalPages = Math.ceil(totalCount / 20);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Redeemable Products</h1>
        <p>Manage reward products across all schemes with scheme tags, search, add and edit controls.</p>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="search-filters">
            <div className="search-box">
              <FiSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search by product name, description or scheme..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <select className="filter-dropdown" value={schemeId} onChange={(e) => { setSchemeId(e.target.value); setPage(1); }}>
              <option value="">All Schemes</option>
              {schemes.map((scheme) => (
                <option key={scheme.schemeId} value={scheme.schemeId}>{scheme.schemeName}</option>
              ))}
            </select>
            <select className="filter-dropdown" value={userType} onChange={(e) => { setUserType(e.target.value); setPage(1); }}>
              <option value="">All Users</option>
              <option value="both">Both</option>
              <option value="dealer">Dealer</option>
              <option value="contractor">Contractor</option>
            </select>
            <select className="filter-dropdown" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <button className="btn btn-outline" onClick={handleSearch}>
              <FiSearch /> Search
            </button>
          </div>
          <button className="btn btn-primary" onClick={openCreate}>
            <FiPlus /> Add Redeemable Product
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading redeemable products...</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Scheme</th>
                  <th>Image</th>
                  <th>MRP</th>
                  <th>Points</th>
                  <th>Stock</th>
                  <th>User Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.catalogId}>
                    <td>
                      <div className="stack-sm">
                        <strong>{item.itemName}</strong>
                        <span className="text-muted">{item.description || 'No description'}</span>
                      </div>
                    </td>
                    <td>
                      <div className="stack-sm">
                        <span className="badge badge-info">{item.schemeName}</span>
                        <span className="text-muted">{item.startDate} to {item.endDate}</span>
                      </div>
                    </td>
                    <td>
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.itemName} className="catalog-thumb" />
                      ) : (
                        <div className="catalog-thumb catalog-thumb-empty">No image</div>
                      )}
                    </td>
                    <td>₹{Number(item.mrp || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>{item.pointsRequired}</td>
                    <td>{item.stockQty}</td>
                    <td><span className="badge badge-secondary">{item.userType}</span></td>
                    <td>
                      <div className="stack-sm">
                        <span className={`badge ${item.isActive ? 'badge-success' : 'badge-danger'}`}>
                          {item.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {!item.schemeActive && <span className="text-muted">Scheme inactive</span>}
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-sm btn-outline" onClick={() => openEdit(item)}>
                          <FiEdit2 />
                        </button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDeactivate(item.catalogId)}>
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center">No redeemable products found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Redeemable Product' : 'Add Redeemable Product'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>

            <div className="form-group">
              <label>Scheme *</label>
              <select
                className="form-control"
                value={form.schemeId}
                onChange={(e) => setForm({ ...form, schemeId: parseInt(e.target.value, 10) || 0 })}
              >
                <option value={0}>Select a scheme</option>
                {schemes.map((scheme) => (
                  <option key={scheme.schemeId} value={scheme.schemeId}>{scheme.schemeName}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Product Name *</label>
              <input
                className="form-control"
                value={form.itemName}
                onChange={(e) => setForm({ ...form, itemName: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                className="form-control"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Product Image</label>
              <input
                className="form-control"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => handleImageUpload(e.target.files?.[0])}
              />
              {form.imageUrl && (
                <div className="catalog-upload-preview">
                  <img src={form.imageUrl} alt="Product preview" className="catalog-thumb" />
                  <input
                    className="form-control"
                    value={form.imageUrl}
                    onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                  />
                </div>
              )}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>MRP</label>
                <input
                  className="form-control"
                  type="number"
                  step="0.01"
                  value={form.mrp}
                  onChange={(e) => setForm({ ...form, mrp: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="form-group">
                <label>Points Required</label>
                <input
                  className="form-control"
                  type="number"
                  value={form.pointsRequired}
                  onChange={(e) => setForm({ ...form, pointsRequired: parseInt(e.target.value, 10) || 0 })}
                />
              </div>
              <div className="form-group">
                <label>Stock Quantity</label>
                <input
                  className="form-control"
                  type="number"
                  value={form.stockQty}
                  onChange={(e) => setForm({ ...form, stockQty: parseInt(e.target.value, 10) || 0 })}
                />
              </div>
              <div className="form-group">
                <label>User Type</label>
                <select
                  className="form-control"
                  value={form.userType}
                  onChange={(e) => setForm({ ...form, userType: e.target.value })}
                >
                  <option value="both">Both</option>
                  <option value="dealer">Dealer</option>
                  <option value="contractor">Contractor</option>
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                <FiPackage /> {editing ? 'Update Product' : 'Add Product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RedeemableProducts;
